import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DatasetShareSetting, AvailableSubscription, ExportTemplate, DatasetUIdsByType } from './dataset.model';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { QueryParams, QueryResult, DocumentQueryParams } from '../../../core/store/models/query.model';
import { HttpService } from '../../../core/services/http.service';
import { DocumentSchema } from '../data/data.model';
import { Attachment } from 'src/app/modules/core/store/models/attachment.model';
import { DatasetsRequest } from 'src/app/modules/core/store/models/dataset.model';
import { UploadedFile } from 'src/app/modules/core/components/file-upload/uploadedFile.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class DatasetApiService {
  constructor(private http: HttpService) {}

  public createDataset(dataset: Dataset): Observable<Dataset> {
    const url = `${apiBaseUrl}/v1/meta/datasets/`;

    return this.http.post(url, dataset);
  }

  public getDatasets(params: QueryParams, includeWorkflow?: boolean, skipChildren?: boolean): Observable<QueryResult<Dataset>> {
    const datasetsRequest = {
      ...params,
      includeWorkflow: !!includeWorkflow,
      skipChildren: !!skipChildren
    } as DatasetsRequest;

    const url = `${apiBaseUrl}/v1/meta/datasets/query`;

    return this.http.post(url, datasetsRequest);
  }

  public getDataset(datasetUId: string, includeWorkflow?: boolean): Observable<Dataset> {
    const queryString = this.http.queryStringFromObject({
      includeWorkflow: !!includeWorkflow
    });

    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}?${queryString}`;

    return this.http.get<Dataset>(url);
  }

  public deleteDataset(dataset: Dataset): Observable<void> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${dataset.uId}`;

    return this.http.delete(url);
  }

  public updateDataset(dataset: Dataset): Observable<Dataset> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${dataset.uId}`;

    return this.http.put(url, dataset);
  }

  public getDatasetSchema(datasetUId: string): Observable<DocumentSchema> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/schema`;

    return this.http.get<DocumentSchema>(url);
  }

  public getDatasetSchemaWithTranslations(datasetUId: string): Observable<DocumentSchema> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/schema.translations`;

    return this.http.get<DocumentSchema>(url);
  }

  public getDatasetShareSettings(datasetUId: string, params: QueryParams): Observable<QueryResult<DatasetShareSetting>> {
    const queryString = this.http.queryStringFromObject({
      ...params
    });

    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/share-settings/?${queryString}`;

    return this.http.get<QueryResult<DatasetShareSetting>>(url);
  }

  public shareDataset(organizationUId: string, datasetUId: string) {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/share-settings/${organizationUId}`;

    return this.http.post(url);
  }

  public unshareDataset(organizationUId: string, datasetUId: string) {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/share-settings/${organizationUId}`;

    return this.http.delete(url);
  }

  public getAvailableDatasetSubscriptions(params: QueryParams): Observable<QueryResult<AvailableSubscription>> {
    if (!params) {
      params = { sortDirection: 'Descending' };
    }
    const queryString = this.http.queryStringFromObject({
      ...params
    });

    const url = `${apiBaseUrl}/v1/meta/datasets/available-subscriptions/?${queryString}`;

    return this.http.get<QueryResult<AvailableSubscription>>(url);
  }

  public subscribeToDataset(datasetUId: string) {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/subscribe`;

    return this.http.post(url);
  }

  public getDatasetExportTemplates(datasetUId: string, params: QueryParams): Observable<QueryResult<ExportTemplate>> {
    const queryString = this.http.queryStringFromObject({
      ...params
    });

    const url = `${apiBaseUrl}/v1/analytics/templates/query?${queryString}`;

    const request = {
      datasetUIds: [datasetUId]
    };

    return this.http.post<QueryResult<ExportTemplate>>(url, request);
  }

  public createExportTemplate(template: ExportTemplate): Observable<ExportTemplate> {
    const url = `${apiBaseUrl}/v1/analytics/templates`;

    return this.http.post(url, template);
  }

  public updateExportTemplate(template: ExportTemplate): Observable<ExportTemplate> {
    const url = `${apiBaseUrl}/v1/analytics/templates`;

    return this.http.put(url, template);
  }

  public deleteExportTemplate(template: ExportTemplate): Observable<void> {
    const url = `${apiBaseUrl}/v1/analytics/templates/${template.uId}/ds/${template.datasetUId}`;

    return this.http.delete(url);
  }

  public getTemplateAttachments(datasetUId: string, templateUId: string): Observable<QueryResult<Attachment>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/files/${templateUId}?skip=0&take=1000&sortField=UpdatedOn&sortDirection=Ascending`;

    return this.http.get<QueryResult<Attachment>>(url);
  }

  public exportDocumentsToXlsx(datasetUId: string, query: DocumentQueryParams, includeWorkflowStepLabel = false): Observable<any> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query/xlsx?includeWorkflowStepLabel=${includeWorkflowStepLabel}`;

    return this.http.post(url, query, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public getDatasetUIdsPerTypes(datasetTypes: { type: DatasetType; subtype: DatasetSubtype }[]): Observable<DatasetUIdsByType[]> {
    const url = `${apiBaseUrl}/v1/meta/datasets/uids-by-types`;

    return this.http.post(url, {
      types: datasetTypes
    });
  }

  public getOldestDatasetHistory(datasetUId: string): Observable<Document> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/oldest-history`;
    return this.http.get<Document>(url);
  }
}
